/**
 * Central catalogue of official CollectorUp certificate + album artwork.
 *
 *   • `certificateSample` — publicly displayable, pre-baked "SAMPLE" watermarked
 *                           image used on `/certificates/{n}`.
 *   • `certificate`       — the OWNER'S ORIGINAL proprietary artwork. Kept in
 *                           the catalogue for FUTURE authenticated-owner PDF
 *                           generation / signed downloads. Never displayed
 *                           publicly by any route today.
 *
 * Adding a future tournament / cover variant is a three-step operation:
 *   1) upload the four assets (HC album cover, SC album cover, HC sample cert,
 *      SC sample cert) AND the two original printable PNGs to the
 *      customer-assets bucket,
 *   2) append a new entry keyed by album_slug below,
 *   3) ensure `certificate` (original) is never referenced from a public
 *      route — the sample field is the only one wired into the public UI.
 */

const ASSETS = {
  // 2026 FIFA World Cup — CAN/MEX/USA
  "can-mex-usa-2026": {
    HC: {
      albumCover:
        "https://customer-assets-lqy194kg.emergentagent.net/job_27cb127a-2886-41a6-886c-e1d3db01f0ff/artifacts/xxnlwugl_HARD%20COVER%202026%20ALBUM.webp",
      certificate:
        // OWNER'S ORIGINAL — never displayed publicly. Reserved for future
        // authenticated-owner PDF generation / signed download endpoint.
        "https://customer-assets-lqy194kg.emergentagent.net/job_27cb127a-2886-41a6-886c-e1d3db01f0ff/artifacts/8tglqwjy_HARD%20COVER%20ORIGINAL%20CERTIFICATE.png",
      certificateSample:
        // Pre-baked SAMPLE — publicly displayable.
        "https://customer-assets-lqy194kg.emergentagent.net/job_27cb127a-2886-41a6-886c-e1d3db01f0ff/artifacts/zevenv6u_HARD%20COVER%20SAMPLE.png",
      certificateAlt: "Official CollectorUp Hardcover Certificate — Sample Preview",
      albumAlt: "Panini FIFA World Cup 2026 Hardcover Sticker Album",
    },
    SC: {
      albumCover:
        "https://customer-assets-lqy194kg.emergentagent.net/job_27cb127a-2886-41a6-886c-e1d3db01f0ff/artifacts/7g085wkf_SOFT%20COVER%202026%20ALBUM.webp",
      certificate:
        // OWNER'S ORIGINAL — never displayed publicly. Reserved for future
        // authenticated-owner PDF generation / signed download endpoint.
        "https://customer-assets-lqy194kg.emergentagent.net/job_27cb127a-2886-41a6-886c-e1d3db01f0ff/artifacts/v00qti20_SOFT%20COVER%20CERTIFICATE%20ORIGINAL.png",
      certificateSample:
        // Pre-baked SAMPLE — publicly displayable.
        "https://customer-assets-lqy194kg.emergentagent.net/job_27cb127a-2886-41a6-886c-e1d3db01f0ff/artifacts/prjucc8m_SOFT%20COVER%20SAMPLE.png",
      certificateAlt: "Official CollectorUp Softcover Certificate — Sample Preview",
      albumAlt: "Panini FIFA World Cup 2026 Softcover Sticker Album",
    },
  },
};

/**
 * Public preview accessor — returns the SAMPLE-watermarked assets safe for
 * public display. The original printable certificate is NOT included in the
 * returned object; use `getOriginalCertificate()` from an authenticated
 * server-side context only.
 *
 * @param {string} albumSlug
 * @param {"HC"|"SC"} albumType
 * @returns {{ albumCover:string, certificate:string, certificateAlt:string, albumAlt:string } | null}
 *   `certificate` in the returned object points to the SAMPLE image so calling
 *   code (e.g. RegistryCertificatePremium) does not need any refactor.
 */
export function getCertificateAssets(albumSlug, albumType) {
  if (!albumSlug || !albumType) return null;
  const entry = ASSETS[albumSlug]?.[albumType];
  if (!entry) return null;
  return {
    albumCover: entry.albumCover,
    certificate: entry.certificateSample, // public-safe swap
    certificateAlt: entry.certificateAlt,
    albumAlt: entry.albumAlt,
  };
}

/**
 * ⚠️  Authenticated-owner use only. Returns the original printable certificate
 * PNG URL. Must NEVER be called from a public route. Reserved for the future
 * signed PDF-download endpoint (Phase 7+). Public routes must always use
 * `getCertificateAssets()` above.
 */
export function getOriginalCertificate(albumSlug, albumType) {
  if (!albumSlug || !albumType) return null;
  return ASSETS[albumSlug]?.[albumType]?.certificate || null;
}
